import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faShieldHalved,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../components/Loader";
import API from "../../api/api";
import { useAuth } from "../../contexts/Auth";
import Button from "../../components/Button";
import OnboardingAlert from "./OnboardingAlert";

// temporarily declaring these here. getting the following error when beam-common is installed
// https://github.com/oven-sh/bun/issues/5122
export enum OnboardingStatus {
  Approved = "Approved",
  Pending = "Pending",
  NotStarted = "Not Started",
  Rejected = "Rejected",
}
export type OnboardingProgress = {
  accountId: string;
  name: string;
  type: "Business" | "Individual";
  terms: {
    status: OnboardingStatus | string;
  };
  kyc: {
    status: OnboardingStatus | string;
  };
  // for individuals only
  walletAddress?: {
    status: OnboardingStatus | string;
  };
};
export default function Onboarding() {
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const [onboardingList, setOnboardingList] = useState<OnboardingProgress[]>(
    [],
  );

  // we are limiting each acct to 1 business for now
  const businessOnboardingProgress = onboardingList.filter(
    (item) => item.type === "Business",
  )[0];
  const businessKycStatus = businessOnboardingProgress?.kyc?.status;

  // we need to look to see if the logged in user has started an onboaridng, could be personal or business
  const getExistingOnboarding = async () => {
    if (!auth.currentUser?.id) {
      return;
    }

    setLoading(true);

    try {
      const { data } = await API.get(
        `business/users/${auth.currentUser.id}/onboarding`,
      );

      // filter only business
      const businessOnboarding = data.filter(
        (item: OnboardingProgress) => item.type === "Business",
      );
      setOnboardingList(businessOnboarding);

      // if the user has selected a business account, we should show that
      if (auth.currentUser?.selectedAccount) {
        const selectedAccount = businessOnboarding.find(
          (item: OnboardingProgress) =>
            item.accountId === auth.currentUser?.selectedAccount?.accountId,
        );
        if (selectedAccount) {
          navigate(`/accounts/${selectedAccount.accountId}`);
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch existing onboarding:", error);
    } finally {
      setLoading(false);
    }
  };
  const startNewOnboarding = async () => {
    setLoading(true);

    try {
      // Check if user is authenticated
      if (!auth.currentUser?.id) {
        console.log("no user id", auth);
        // refresh page
        window.location.reload();
        return;
      }

      // we need to create the new f account for the business and tie this user to it
      // get back the id and navigate to a page to onboard with it
      const { data } = await API.post(
        `business/users/${auth.currentUser.id}/onboarding`,
        { isBiz: true },
      );

      auth.updateUser({ ...auth.currentUser, selectedAccount: data });
      navigate(`/accounts/${data.accountId}`);
    } catch (error: any) {
      console.error("Onboarding failed:", error);
    } finally {
      setLoading(false); // Always reset loading state
    }
  };

  const handleSubmitOnboarding = async () => {
    setOpenAlert(true);
  };

  useEffect(() => {
    getExistingOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser?.id]);

  const viewOnboardingDetails = (accountId: string) => {
    const name = businessOnboardingProgress?.name;
    const status = businessKycStatus;
    navigate(`/accounts/${accountId}`, {
      state: { status, name, id: accountId },
    });
  };

  return (
    <>
      <OnboardingAlert
        open={openAlert}
        setOpen={setOpenAlert}
        onAccept={startNewOnboarding}
      />
      <div className="grid grid-cols-2 gap-2">
        <div className="flex justify-between items-center p-6 h-auto col-span-2">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Accounts
          </h3>
          {!loading && businessKycStatus !== OnboardingStatus.Approved && (
            <Button
              text={`${
                !!businessOnboardingProgress ? "Continue" : "Start"
              } Onboarding`}
              loading={loading}
              disabled={
                loading || businessKycStatus === OnboardingStatus.Approved
              }
              onSubmit={
                !!businessOnboardingProgress
                  ? () =>
                      viewOnboardingDetails(
                        businessOnboardingProgress.accountId,
                      )
                  : handleSubmitOnboarding
              }
            ></Button>
          )}
        </div>

        {onboardingList.length === 0 && !loading ? (
          <div className="col-span-2 px-6">
            <div className="max-w-2xl mx-auto mt-8 text-center">
              <h2 className="text-xl font-medium mb-4">
                Complete Your Account Setup
              </h2>
              <p className="text-gray-600 mb-8">
                Thanks for telling us about your business! To start using Beam's
                payment features, you'll need to complete a quick compliance
                verification process.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 bg-white rounded-lg shadow-sm">
                  <FontAwesomeIcon
                    icon={faClipboardCheck}
                    className="text-[#6333A4] text-2xl mb-3"
                  />
                  <h3 className="font-medium mb-2">What You'll Need</h3>
                  <p className="text-sm text-gray-600">
                    Government-issued ID and proof of business ownership
                  </p>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-sm">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="text-[#6333A4] text-2xl mb-3"
                  />
                  <h3 className="font-medium mb-2">Quick Process</h3>
                  <p className="text-sm text-gray-600">
                    Complete verification in under 10 minutes
                  </p>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-sm">
                  <FontAwesomeIcon
                    icon={faShieldHalved}
                    className="text-[#6333A4] text-2xl mb-3"
                  />
                  <h3 className="font-medium mb-2">Secure & Compliant</h3>
                  <p className="text-sm text-gray-600">
                    Bank-grade security and regulatory compliance
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-sm text-gray-600">
                  Click the Start Onboarding button above to begin. This
                  verification step is required to activate your account
                  features and ensure secure transactions.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 sm:px-6 lg:px-8 col-span-2">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto"></div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none"></div>
            </div>
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 mt-4">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Terms
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        KYB
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {onboardingList.map((businessOnboardingProgress) => (
                      <tr key={businessOnboardingProgress.accountId}>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {businessOnboardingProgress.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {businessOnboardingProgress.terms.status}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4  text-sm text-gray-500">
                          {businessOnboardingProgress.kyc.status}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <button
                            className="text-lavender-700 hover:text-lavender-700"
                            onClick={() =>
                              viewOnboardingDetails(
                                businessOnboardingProgress.accountId,
                              )
                            }
                          >
                            View<span className="sr-only"></span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {loading && <Loader />}
      </div>
    </>
  );
}
